import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { moviesData, categories, subCategories, categoryMap, subCategoryMap } from '@/mocks/movies';

type SortType = 'default' | 'rating' | 'year' | 'views';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSubCategory, setActiveSubCategory] = useState('all');
  const [activeCategory, setActiveCategory] = useState(slug || 'movie');
  const [sortBy, setSortBy] = useState<SortType>('default');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setActiveSubCategory('all');
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory, activeSubCategory]);

  const filteredMovies = useMemo(() => {
    let result = moviesData.filter(m => categoryMap[activeCategory] === m.category);
    if (activeSubCategory !== 'all') {
      result = result.filter(m => subCategoryMap[activeSubCategory] === m.subCategory);
    }
    if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      result = [...result].sort((a, b) => b.year - a.year);
    } else if (sortBy === 'views') {
      result = [...result].sort((a, b) => b.views - a.views);
    }
    return result;
  }, [activeCategory, activeSubCategory, sortBy]);

  const categoryName = categoryMap[activeCategory] || '电影';

  return (
    <>
      <header>
        <Navbar />
      </header>

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 py-3">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="bg-background-50 py-8 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
              {categoryName}
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Filter Bar */}
        <section className="bg-background-50 py-4 border-b border-background-200/70 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                {subCategories.map((sub) => (
                  <button
                    key={sub.slug}
                    onClick={() => setActiveSubCategory(sub.slug)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSubCategory === sub.slug
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                {([
                  { label: '默认', value: 'default' as SortType },
                  { label: '评分最高', value: 'rating' as SortType },
                  { label: '最新发布', value: 'year' as SortType },
                  { label: '最多观看', value: 'views' as SortType },
                ]).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="py-8 md:py-10 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-800">{filteredMovies.length}</strong> 部作品
              </p>
            </div>
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} {...movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                  <i className="ri-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${categoryName} - 97影视`,
            "description": `97影视${categoryName}频道，海量${categoryName}资源高清免费在线观看，极速秒播无广告。`,
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "isPartOf": {
              "@type": "WebSite",
              "name": "97影视",
              "url": typeof window !== 'undefined' ? window.location.origin : '',
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": typeof window !== 'undefined' ? window.location.origin + '/' : '/' },
              { "@type": "ListItem", "position": 2, "name": categoryName },
            ]
          })
        }}
      />
    </>
  );
}